import { createFileRoute, Link } from "@tanstack/react-router";
import { Rocket, CheckCircle2 } from "lucide-react";

export const Route = createFileRoute("/bookingsuccess")({
  head: () => ({
    meta: [
      { title: "Booking Success — Three Day Launch" },
      { name: "description", content: "Your website design project is officially booked. Check your email for next steps." },
      { name: "robots", content: "noindex" },
      { property: "og:title", content: "Booking Success — Three Day Launch" },
      { property: "og:description", content: "Your website design project is officially booked. Check your email for next steps." },
    ],
    scripts: [
      {
        children: "gtag('event', 'conversion', {'send_to': 'AW-11495268868/5RR_CNPZ3aAcEITEr-kq','transaction_id': ''});",
      },
    ],
  }),
  component: BookingSuccessPage,
});

function BookingSuccessPage() {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="border-b border-border">
        <div className="mx-auto max-w-7xl px-6 py-5 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="h-8 w-8 rounded-lg bg-primary-gradient flex items-center justify-center">
              <Rocket className="h-4 w-4 text-white" />
            </div>
            <span className="font-bold">Three Day Launch<sup className="text-[0.6em] ml-0.5">®</sup></span>
          </Link>
          <Link to="/" className="text-sm text-muted-foreground hover:text-foreground">← Back home</Link>
        </div>
      </header>

      <main>
        <section className="py-20 md:py-28">
          <div className="mx-auto max-w-3xl px-6 text-center">
            <div className="inline-flex items-center gap-2 rounded-full bg-primary/10 px-4 py-1.5 text-xs font-bold uppercase tracking-wider text-primary">
              <CheckCircle2 className="h-4 w-4" /> Booking Confirmed
            </div>
            <h1 className="mt-5 text-5xl md:text-7xl font-bold tracking-tight">
              <span className="text-primary">Success!</span>
            </h1>
            <p className="mt-6 text-xl md:text-2xl font-semibold">
              Your website design project is officially booked!
            </p>
            <p className="mt-4 text-lg md:text-xl text-muted-foreground">
              I cannot WAIT to see your newly launched online home.
            </p>
          </div>
        </section>

        <section className="pb-24">
          <div className="mx-auto max-w-3xl px-6">
            <div className="rounded-3xl border border-border bg-card p-8 md:p-12 shadow-card-soft">
              <h2 className="text-2xl md:text-3xl font-bold tracking-tight mb-6">
                Important Next Steps:
              </h2>
              <ol className="space-y-5 text-base md:text-lg text-muted-foreground list-decimal list-inside">
                <li>
                  <span className="text-foreground font-semibold">Check your email</span> for an official confirmation from{" "}
                  <a href="mailto:hello@threedaylaunch.com" className="text-primary font-semibold hover:underline">
                    hello@threedaylaunch.com
                  </a>
                  .
                </li>
                <li>
                  That email will have the subject line{" "}
                  <span className="text-foreground font-semibold">"Congrats! Important Website Launch Next Steps"</span>{" "}
                  and will contain your Blueprint Questionnaire form, asking you for details about your business.
                </li>
                <li>
                  You must complete that blueprint questionnaire{" "}
                  <span className="text-foreground font-semibold">within 7 business days</span> in order for me to begin your website design!
                </li>
                <li>
                  After you complete that questionnaire, you will be emailed a link to{" "}
                  <span className="text-foreground font-semibold">book your kick-off Zoom call</span>.
                </li>
              </ol>
              <p className="mt-8 text-center text-xl font-semibold">Thank you!</p>
            </div>
          </div>
        </section>
      </main>

      <footer className="bg-secondary text-secondary-foreground border-t border-white/10 py-10">
        <div className="mx-auto max-w-7xl px-6 flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <div className="h-8 w-8 rounded-lg bg-primary-gradient flex items-center justify-center">
              <Rocket className="h-4 w-4 text-white" />
            </div>
            <span className="font-bold">Three Day Launch<sup className="text-[0.6em] ml-0.5">®</sup></span>
          </div>
          <nav className="flex items-center gap-6 text-sm text-white/70">
            <Link to="/about" className="hover:text-white transition-colors">About</Link>
            <Link to="/contact" className="hover:text-white transition-colors">Contact</Link>
            <Link to="/privacy-policy" className="hover:text-white transition-colors">Privacy Policy</Link>
            <Link to="/terms-and-conditions" className="hover:text-white transition-colors">Terms &amp; Conditions</Link>
          </nav>
          <div className="text-sm text-white/80">© {new Date().getFullYear()} Three Day Launch. Custom websites for business owners.</div>
        </div>
      </footer>
    </div>
  );
}
